/**
 * Reset Password Page Component
 * Handles password reset from email link
 */
import React, { useState } from 'react';
import { useTheme } from '../ThemeContext';
import { getApiUrl } from '../utils/platform';

const ResetPassword = ({ token, type, onBack }) => {
  const { colors, isDark } = useTheme();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      // Both agents and clients use custom token flow now
      const response = await fetch(getApiUrl('/.netlify/functions/verify-reset-token'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword,
          type // 'agent' or 'client'
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.error || 'Failed to reset password');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setError('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="app-scroll app-scroll-center" style={{...styles.container, background: colors.background}}>
        <div style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`}}>
          <div style={{textAlign: 'center', margin: '2rem 0'}}>
            <img
              src={isDark ? '/images/logo/pacarion-logo-dark.png' : '/images/logo/pacarion-logo-light.png'}
              alt="Pacarion"
              style={{height: '80px', width: 'auto', marginBottom: '0.5rem'}}
            />
            <p style={{...styles.subtitle, color: colors.textSecondary, margin: 0}}>Home maintenance, made simple</p>
          </div>

          <div style={{...styles.successIcon, color: '#59c4c4'}}>✓</div>
          <h2 style={{...styles.successTitle, color: colors.text}}>Password Reset Successfully!</h2>
          <p style={{...styles.successMessage, color: colors.textSecondary}}>
            Your password has been updated. You can now log in with your new password.
          </p>

          <button
            onClick={onBack}
            style={{...styles.button, background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)', color: 'white'}}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-scroll app-scroll-center" style={{...styles.container, background: colors.background}}>
      <div style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`}}>
        <div style={{textAlign: 'center', margin: '2rem 0'}}>
          <img
            src={isDark ? '/images/logo/pacarion-logo-dark.png' : '/images/logo/pacarion-logo-light.png'}
            alt="Pacarion"
            style={{height: '80px', width: 'auto', marginBottom: '0.5rem'}}
          />
          <p style={{...styles.subtitle, color: colors.textSecondary, margin: 0}}>Home maintenance, made simple</p>
        </div>

        <h2 style={{...styles.title, color: colors.text}}>Create New Password</h2>
        <p style={{...styles.description, color: colors.textSecondary}}>
          Enter your new password below.
        </p>

        {error && (
          <div style={{...styles.errorBox, background: '#fee', border: '1px solid #fcc', color: '#c00'}}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="New Password (min 6 characters)"
            style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
            required
            minLength={8}
          />

          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="New password again"
            style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
            required
            minLength={8}
          />

          <button
            type="submit"
            style={{...styles.button, background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)', color: 'white'}}
            disabled={loading}
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <button
          onClick={onBack}
          style={{...styles.backLink, color: colors.textSecondary}}
        >
          ← Back to Login
        </button>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    padding: '2rem 1rem'
  },
  card: {
    padding: '3rem',
    borderRadius: '20px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
    textAlign: 'center',
    maxWidth: '480px',
    width: '100%'
  },
  subtitle: {
    fontSize: '0.9rem',
    marginBottom: '2rem',
    fontWeight: 300,
    letterSpacing: '0.03em'
  },
  title: {
    fontSize: '1.8rem',
    marginBottom: '0.5rem',
    fontWeight: 400,
    letterSpacing: '0.02em'
  },
  description: {
    fontSize: '0.95rem',
    marginBottom: '2rem',
    lineHeight: '1.6'
  },
  input: {
    width: '100%',
    padding: '0.9rem 1.1rem',
    fontSize: '1rem',
    borderRadius: '12px',
    marginBottom: '1rem',
    textAlign: 'left',
    boxSizing: 'border-box',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit'
  },
  button: {
    width: '100%',
    padding: '1rem',
    fontSize: '1rem',
    fontWeight: 400,
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    marginBottom: '1rem',
    transition: 'all 0.2s ease',
    letterSpacing: '0.02em',
    boxShadow: '0 2px 8px rgba(89, 196, 196, 0.2)'
  },
  errorBox: {
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    fontSize: '0.9rem',
    textAlign: 'left'
  },
  backLink: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.9rem',
    padding: '0.5rem',
    fontWeight: 300,
    letterSpacing: '0.02em'
  },
  successIcon: {
    fontSize: '5rem',
    marginBottom: '1rem',
    fontWeight: 'bold'
  },
  successTitle: {
    fontSize: '1.8rem',
    marginBottom: '1rem',
    fontWeight: 400
  },
  successMessage: {
    fontSize: '1rem',
    lineHeight: '1.6',
    marginBottom: '2rem'
  }
};

export default ResetPassword;
